/*
  Interactive colour swatches on product cards (featured collection).

  There was no existing implementation to reuse: the theme ships a legacy `<card-variant-selects>`
  wrapper with radio inputs and a variants JSON payload, but no custom element is ever registered
  for it (51 elements are defined across assets/*.js; none is card-variant-selects), so that markup
  is inert. This file supplies the behaviour.

  Colour -> image resolution deliberately reuses assets/media-gallery.js's rules verbatim, so a
  card and the product gallery always agree on which images belong to a colour:
    - normalizeColor: trim, lowercase, whitespace -> hyphens
    - getMediaTags:   /#([a-z0-9-]+)/g over the image's alt text
    - an image with NO #tags matches every colour; otherwise it must carry the colour's tag

  Selecting a colour updates the card image, the product links (?variant=<id>) and, when the
  variants actually differ, the price and compare-at price. The favorites badge is deliberately
  untouched: it belongs to the product, not the variant.
*/
(function () {
  if (window.__cardSwatchesInitialised) return;
  window.__cardSwatchesInitialised = true;

  function normalizeColor(value) {
    return (value || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
  }

  function getMediaTags(altText) {
    var matches = (altText || '').toLowerCase().matchAll(/#([a-z0-9-]+)/g);
    return Array.from(matches, function (m) { return m[1]; });
  }

  function mediaForColor(media, colorValue) {
    var normalized = normalizeColor(colorValue);
    if (!normalized) return media.slice();
    return media.filter(function (m) {
      var tags = getMediaTags(m.alt);
      return tags.length === 0 || tags.indexOf(normalized) !== -1;
    });
  }

  function readData(card) {
    var node = card.querySelector('script[data-card-swatch-data]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      console.log('card swatches: bad payload -', e);
      return null;
    }
  }

  function applyColor(card, colorValue) {
    var data = readData(card);
    if (!data) return;

    var entry = null;
    for (var i = 0; i < data.colors.length; i++) {
      if (data.colors[i].value === colorValue) { entry = data.colors[i]; break; }
    }

    // ---- image ----
    var pool = mediaForColor(data.media || [], colorValue);
    var chosen = null;
    if (pool.length) {
      // Honour the section's Card image setting within this colour's set.
      chosen = data.imageIndex === 'second' && pool.length > 1 ? pool[1] : pool[0];
    } else if (entry && entry.vimg) {
      chosen = { src: entry.vimg, srcset: '', w: null, h: null };
    } else if ((data.media || []).length) {
      chosen = data.media[0];
    }

    var img = card.querySelector('.card__media img:not([data-card-hover-image])');
    if (img && chosen) {
      // Drop srcset first so the browser cannot keep serving a candidate from the old set.
      img.removeAttribute('srcset');
      if (chosen.srcset) img.setAttribute('srcset', chosen.srcset);
      img.setAttribute('src', chosen.src);
    }

    if (!entry) return;

    // ---- links ----
    card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
      // Leave the collection link alone; it points at a collection, not this product.
      if (a.classList.contains('card__collection-link')) return;
      a.setAttribute('href', entry.url);
    });

    // ---- price, only when the variants actually differ ----
    var prices = data.colors.map(function (c) { return c.price; });
    var compares = data.colors.map(function (c) { return c.compare; });
    var pricesVary = prices.some(function (p) { return p !== prices[0]; });
    var comparesVary = compares.some(function (c) { return c !== compares[0]; });

    if (pricesVary) {
      card.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
        .forEach(function (el) { el.textContent = entry.price; });
    }
    if (comparesVary) {
      card.querySelectorAll('.price__compare-price .price-item--regular')
        .forEach(function (el) { el.textContent = entry.compare; });
    }
  }

  // ---- hover cross-fade target ----------------------------------------------------------
  // Resolved against the SAME colour-filtered set the card image uses, so "Image 2" means the
  // second image of the selected colour, and "Next image" means the one after whatever is
  // currently displayed within that colour's set. Recalculated whenever a swatch changes.
  function currentIndexIn(pool, img) {
    if (!img) return -1;
    var current = (img.getAttribute('src') || '').split('?')[0];
    for (var i = 0; i < pool.length; i++) {
      if ((pool[i].src || '').split('?')[0] === current) return i;
    }
    return -1;
  }

  function applyHoverTarget(card, colorValue) {
    var hover = card.querySelector('[data-card-hover-image]');
    if (!hover) return;
    var data = readData(card);
    if (!data) return;

    var pool = mediaForColor(data.media || [], colorValue);
    var primary = card.querySelector('.card__media img:not([data-card-hover-image])');

    // A single-image set has nothing to fade to.
    if (pool.length < 2) {
      hover.removeAttribute('data-ready');
      hover.removeAttribute('src');
      return;
    }

    var mode = data.hoverImage || 'next';
    var target = null;
    if (mode === 'next') {
      var idx = currentIndexIn(pool, primary);
      target = pool[(idx + 1 + pool.length) % pool.length];
    } else {
      var wanted = parseInt(mode, 10);
      // Explicit slots are 1-based; fall back to Next image when the set is too short.
      if (!isNaN(wanted) && pool[wanted - 1]) {
        target = pool[wanted - 1];
      } else {
        var i2 = currentIndexIn(pool, primary);
        target = pool[(i2 + 1 + pool.length) % pool.length];
      }
    }
    // Never fade to the image already showing.
    if (target && primary && (target.src || '').split('?')[0] === (primary.getAttribute('src') || '').split('?')[0]) {
      var j = currentIndexIn(pool, primary);
      target = pool[(j + 1 + pool.length) % pool.length];
    }
    if (!target) {
      hover.removeAttribute('data-ready');
      return;
    }
    // Preload so the first hover is never blank, and only enable the fade once decoded.
    var pre = new Image();
    pre.onload = function () {
      hover.setAttribute('src', target.src);
      if (target.srcset) hover.setAttribute('srcset', target.srcset);
      hover.setAttribute('data-ready', 'true');
    };
    pre.src = target.src;
  }

  function initCards(root) {
    (root || document).querySelectorAll('.card-wrapper').forEach(function (card) {
      if (!card.querySelector('[data-card-hover-image]')) return;
      var checked = card.querySelector('input[data-card-swatch]:checked');
      applyHoverTarget(card, checked ? checked.dataset.color : '');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initCards(); });
  } else {
    initCards();
  }
  document.addEventListener('shopify:section:load', function (e) { initCards(e.target); });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('input[data-card-swatch]');
    if (!input) return;
    var card = input.closest('.card-wrapper');
    if (!card) return;
    applyColor(card, input.dataset.color);
    applyHoverTarget(card, input.dataset.color);
  });

  // The card title's .full-unstyled-link::after paints a transparent whole-card overlay. The
  // label is lifted above it in CSS, but a click that lands on the row's padding would otherwise
  // fall through to the product link, so swatch clicks are contained here.
  document.addEventListener('click', function (event) {
    var swatch = event.target.closest('.card__native-swatches .color-swatch');
    if (!swatch) return;
    event.preventDefault();
    event.stopPropagation();
    var input = swatch.querySelector('input[data-card-swatch]');
    if (input && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  });
})();
